import React, { useState, useEffect } from "react";
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardHeader,
    CardBody,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
} from "reactstrap";
import Select from 'react-select'; // Import react-select
import Header from '../../components/Headers/Header';
import ViewLeaveRequestModal from '../../components/Modals/ViewLeaveRequest';
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaTimes } from "react-icons/fa";
import { apiURL1 } from '../../config';
import 'jspdf-autotable';
import Footer from "components/Footers/Footer";

const baseURL = apiURL1;

function TeacherLeave() {
    const token = localStorage.getItem("token");

    const [leaveModal, setLeaveModal] = useState(false);
    const [leaveType, setLeaveType] = useState(null); // Change to null for react-select
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null); // Change to null for consistency
    const [leaveReason, setLeaveReason] = useState('');
    const [leaveApplications, setLeaveApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [currentLeave, setCurrentLeave] = useState(null);
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);
    const itemsPerPage = 10;
    const user = localStorage.getItem('userInfo');
    const userRole = localStorage.getItem('role');
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        if (user) {
            const parsedUser = JSON.parse(user);
            setUserId(parsedUser._id);
        }
    }, [user]);

    const fetchLeaves = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            let url = `${apiURL1}/${userRole}/${userId}/leaves?page=${currentPage}&limit=${itemsPerPage}`;
            if (leaveType) url += `&leaveType=${leaveType.value}`; // Use leaveType.value for react-select
            if (filterStatus) url += `&status=${filterStatus}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.data && response.data.leaves) {
                setLeaveApplications(response.data.leaves);
                setTotalPages(Math.ceil(response.data.totalLeaves / itemsPerPage));
            } else {
                setLeaveApplications([]);
                setTotalPages(0);
            }
        } catch (error) {
            toast.error('Failed to fetch leave applications.');
            setLeaveApplications([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeaveTypes = async () => {
        try {
            const response = await axios.get(
                `${apiURL1}/${userRole}/leave/types`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            // Map leave types to react-select format
            const formattedLeaveTypes = response.data.map(type => ({
                value: type._id,
                label: type.name
            }));
            setLeaveTypes(formattedLeaveTypes);
        } catch (error) {
            toast.error('Failed to fetch leave types.');
        }
    };

    useEffect(() => {
        fetchLeaves();
        fetchLeaveTypes();
    }, [userId, currentPage, leaveType, filterStatus]);

    const toggleLeaveModal = () => setLeaveModal(!leaveModal);
    const toggleViewModal = () => setViewModalOpen(!viewModalOpen);
    const toggleWithdrawModal = () => setWithdrawModalOpen(!withdrawModalOpen);

    const handleLeaveTypeChange = (selectedOption) => {
        setLeaveType(selectedOption);
        setCurrentPage(1);
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const handleLeaveReasonChange = (e) => {
        setLeaveReason(e.target.value);
    };

    const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDates = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const calculateDays = () => {
        if (!startDate || !endDate) return 0;
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays + 1;
    };

    const handleSubmitLeave = async (e) => {
        e.preventDefault();
        if (!userId) {
            toast.error('User ID not found.');
            return;
        }
        setLoading(true);
        try {
            const formattedStartDate = formatDate(startDate);
            const formattedEndDate = formatDate(endDate);

            await axios.post(`${apiURL1}/${userRole}/${userId}/leaves`, {
                leaveType: leaveType ? leaveType.value : '', // Use leaveType.value
                fromDate: formattedStartDate,
                toDate: formattedEndDate,
                leaveReason,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            fetchLeaves();
            toast.success('Leave application submitted successfully!');
            toggleLeaveModal();
            setLeaveType(null); // Reset to null
            setEndDate(null);
            setStartDate(null);
            setLeaveReason('');
        } catch (error) {
            toast.error('Failed to submit leave application.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelWithdraw = async (leaveId, action) => {
        if (!userId) {
            toast.error('User ID not found.');
            return;
        }
        try {
            await axios.put(
                `${apiURL1}/${userRole}/leave/${action}/${userId}/${leaveId}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            fetchLeaves();
            toast.success(`Leave application ${action} successfully!`);
        } catch (error) {
            toast.error(`Failed to ${action} leave application.`);
        }
        toggleWithdrawModal();
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength) + '...';
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPagination = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <PaginationItem key={i} active={i === currentPage}>
                    <PaginationLink onClick={() => handlePageChange(i)}>
                        {i}
                    </PaginationLink>
                </PaginationItem>,
            );
        }
        return (
            <Pagination className="justify-content-center">
                <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                        previous
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                </PaginationItem>
                {pages}
                <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                        next
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </PaginationItem>
            </Pagination>
        );
    };

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Leave Management</h3>
                                <Button color="primary" size="sm" onClick={toggleLeaveModal}>
                                    Add New
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Search by leave type</Label>
                                            <Select
                                                value={leaveType}
                                                onChange={handleLeaveTypeChange}
                                                options={[
                                                    { value: '', label: 'All Leave Types' },
                                                    ...leaveTypes
                                                ]}
                                                placeholder="Select Leave Type"
                                                isSearchable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Status</Label>
                                            <Input
                                                type="select"
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="">All Status</option>
                                                <option value="pending">Pending</option>
                                                <option value="approved">Approved</option>
                                                <option value="withdrawn">Withdrawn</option>
                                                <option value="rejected">Canceled</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {loading ? (
                                    <div className="text-center">
                                        <Spinner color="primary" />
                                    </div>
                                ) : (
                                    <Table className="align-items-center table-flush" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Leave Type</th>
                                                <th>Leave Duration</th>
                                                <th>Leave Reason</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leaveApplications.length > 0 ? (
                                                leaveApplications.map((app) => (
                                                    <tr key={app._id}>
                                                        <td>{app?.leaveType?.name || app?.leaveType || '-'}</td>
                                                        <td>
                                                            {formatDates(app.fromDate)} -{' '}
                                                            {formatDates(app.toDate)}
                                                        </td>
                                                        <td>{truncateText(app.leaveReason, 30)}</td>
                                                        <td>{app.status}</td>
                                                        <td>
                                                            <div className="d-flex">
                                                                <Button
                                                                    size="sm"
                                                                    color="info"
                                                                    onClick={() => {
                                                                        setCurrentLeave(app);
                                                                        toggleViewModal();
                                                                    }}
                                                                >
                                                                    <i className="fa-solid fa-eye"></i>
                                                                </Button>
                                                                {app.status === 'pending' && (
                                                                    <Button
                                                                        color="warning"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setSelectedLeaveId(app._id);
                                                                            toggleWithdrawModal();
                                                                        }}
                                                                    >
                                                                        <i className="fa-solid fa-arrow-right-from-bracket"></i>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                                {renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Leave Application Modal */}
                <Modal isOpen={leaveModal} toggle={toggleLeaveModal}>
                    <ModalHeader className="pb-0">
                        Apply for Leave
                        <Button
                            className="close"
                            onClick={toggleLeaveModal}
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                        >
                            <FaTimes />
                        </Button>
                    </ModalHeader>
                    <ModalBody>
                        <Form onSubmit={handleSubmitLeave}>
                            <FormGroup>
                                <Label>Leave Type</Label>
                                <Select
                                    value={leaveType}
                                    onChange={handleLeaveTypeChange}
                                    options={leaveTypes}
                                    placeholder="Select Leave Type"
                                    isSearchable
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Reason</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter leave reason"
                                    value={leaveReason}
                                    onChange={handleLeaveReasonChange}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Days</Label>
                                <div className="d-flex">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={handleStartDateChange}
                                        selectsStart
                                        startDate={startDate}
                                        endDate={endDate}
                                        placeholderText="Start Date"
                                        className="form-control mr-2"
                                    />
                                    <DatePicker
                                        selected={endDate}
                                        onChange={handleEndDateChange}
                                        selectsEnd
                                        startDate={startDate}
                                        endDate={endDate}
                                        minDate={startDate}
                                        placeholderText="End Date"
                                        className="form-control"
                                    />
                                </div>
                            </FormGroup>
                            <div className="d-flex justify-content-end">
                                <Button color="primary" type="submit" disabled={loading}>
                                    {loading ? <Spinner size="sm" /> : 'Submit'}
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>
                <ViewLeaveRequestModal
                    isOpen={viewModalOpen}
                    toggle={toggleViewModal}
                    leave={currentLeave}
                />
                <Modal isOpen={withdrawModalOpen} toggle={toggleWithdrawModal}>
                    <ModalHeader toggle={toggleWithdrawModal}>
                        Withdraw Leave Request
                    </ModalHeader>
                    <ModalBody>
                        Are you sure you want to withdraw this leave request?
                        <div className="d-flex justify-content-end mt-3">
                            <Button
                                color="secondary"
                                onClick={toggleWithdrawModal}
                                className="mr-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                color="warning"
                                onClick={() => handleCancelWithdraw(selectedLeaveId, 'withdraw')}
                            >
                                Withdraw
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>
                <Footer />
            </Container>
        </>
    );
}

export default TeacherLeave;